import React, { useState, useEffect, useCallback } from 'react';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { BorrowerApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import store, { borrowerActions } from '@apply/adminStore';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTimes, faPlus } from '@fortawesome/free-solid-svg-icons';

export const CompanySelector = ({
    companyId,
    onSelect = null,
    disabled = false,
    required = true,
    canEdit = true,
    showTitle = true,
    title = 'Company',
    isValid = true,
}) => {
    const PER_PAGE = 20;
    const borrowerApi = new BorrowerApi.BorrowerApi(applicationInsights);
    const { dispatch } = store;

    //Borrower Search
    const [cache, set_cache] = useState({});
    const [searchLoading, set_searchLoading] = useState(false);
    const [companies, set_companies] = useState([]);
    const [query, set_query] = useState('');
    const [company, set_company] = useState();

    useEffect(() => {
        // Do we need to await when no code follows?
        async function load() {
            await loadCompany(companyId);
        }

        load();
    }, [companyId]);

    const loadCompany = async (id) => {
        if (!id) {
            set_company(null);
            return;
        }

        const response = await borrowerApi.companiesTypeAheadById(id);
        if (response.statusCode !== 200) return;
        const bwr = response.result;
        set_company(bwr);
        return bwr;
    };

    const addCompany = () => {
        dispatch(
            borrowerActions.setEditCompanyModal({
                editCompanyModal: {
                    isOpen: true,
                    companyId: 0,
                    onChange: afterSave,
                },
            })
        );
    };

    const doSearch = async (query, page = 1) => {
        const model = {
            searchText: query,
            take: PER_PAGE,
            skip: (page - 1) * PER_PAGE,
        };
        return await borrowerApi.companiesTypeAhead(model);
    };

    const handleInputChange = (q) => {
        set_company();
        q = (q ?? '').toLowerCase();
        set_query(q);
    };

    const handleSearch = useCallback((q) => {
        q = (q ?? '').toLowerCase();
        if (cache[q]) {
            set_companies(cache[q].options);
            return;
        }

        set_searchLoading(true);
        doSearch(q).then((resp) => {
            cache[q] = { options: resp.result, page: 1 };
            set_cache(cache);
            set_searchLoading(false);
            set_companies(resp.result);
        });
    }, []);

    const handlePagination = (e, shownResults) => {
        const cachedQuery = cache[query];

        // Don't make another request if:
        // - the cached results exceed the shown results
        // - we've already fetched all possible results
        if (cachedQuery.options.length > shownResults || cachedQuery.options.length === cachedQuery.total_count) {
            return;
        }

        set_searchLoading(true);
        const page = cachedQuery.page + 1;
        doSearch(query, page).then((resp) => {
            const options = cachedQuery.options.concat(resp.result);
            cache[query] = { ...cachedQuery, options, page };
            set_cache(cache);
            set_searchLoading(false);
            set_companies(options);
        });
    };

    const onCompanySelect = async (cmpy) => {
        const fullCompany = await loadCompany(cmpy?.companyId);
        onSelect && onSelect(fullCompany, cancelSelect);
    };

    const cancelSelect = () => {
        set_company(null);
    };

    const clearCompany = () => {
        set_company(null);
        onSelect && onSelect(null, cancelSelect);
    };

    const afterSave = (enty) => {
        onCompanySelect(enty);
        set_cache({});
    };

    const editCompany = (companyId) => {
        dispatch(
            borrowerActions.setEditCompanyModal({
                editCompanyModal: {
                    isOpen: true,
                    companyId,
                    onChange: afterSave,
                },
            })
        );
    };

    const filterByFields = ['name'];

    return (
        <div style={{ width: '98%' }}>
            <div className='type-ahead-container'>
                {showTitle && (
                    <label htmlFor='company-search'>
                        {title} {required && <span className='req'>*</span>}
                    </label>
                )}
                <AsyncTypeahead
                    className={!isValid ? 'type-ahead not-valid' : 'type-ahead'}
                    id='company-search'
                    isLoading={searchLoading}
                    labelKey={(option) => `${option?.name}`}
                    maxResults={PER_PAGE - 1}
                    minLength={2}
                    onInputChange={handleInputChange}
                    onPaginate={handlePagination}
                    onSearch={handleSearch}
                    onChange={(event) => onCompanySelect(event[0])}
                    options={companies}
                    paginate
                    placeholder='Search By Name...'
                    renderMenuItemChildren={(option) => (
                        <div
                            key={option.id}
                            style={{ borderBottom: '1px solid silver' }}
                        >
                            <span>
                                <div>{option.name}</div>
                                {(option.city || option.state) && (
                                    <span>
                                        {option?.city}, {option?.state}
                                    </span>
                                )}
                            </span>
                        </div>
                    )}
                    useCache={false}
                    option
                    selected={company ? [company] : []}
                    disabled={disabled}
                    inputProps={{ required: required }}
                    filterBy={filterByFields}
                />
                <button
                    type='button'
                    className='type-ahead-clear-button'
                    onClick={() => clearCompany()}
                    disabled={disabled}
                    title='Remove selected company'
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
                <button
                    type='button'
                    onClick={addCompany}
                    className='button type-ahead-add-button'
                    title='Add a new company'
                    disabled={disabled}
                >
                    <FontAwesomeIcon icon={faPlus} />
                </button>
            </div>

            {company && (
                <div
                    style={{
                        border: '1px solid silver',
                        borderRadius: '10px',
                        padding: '10px',
                        backgroundColor: '#f0f8ff',
                        width: '100%',
                    }}
                >
                    <div className='row'>
                        <div className='columns small-12 large-5'>
                            {canEdit && (
                                <div style={{ float: 'right' }}>
                                    <button
                                        type='button'
                                        onClick={() => editCompany(company.companyId)}
                                        className='button hollow'
                                        title='Edit Borrower Details'
                                        style={{
                                            verticalAlign: 'unset',
                                            marginBottom: '0',
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faPencil} />
                                    </button>
                                </div>
                            )}
                            {company.name && (
                                <div>
                                    <b>{company.name}</b>
                                </div>
                            )}
                            {company.street1 && <div>{company.street1}</div>}
                            {company.street2 && <div>{company.street2}</div>}
                            {company.city && company.state && company.zipCode && (
                                <div>
                                    {company.city}, {company.state} {company.zipCode}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
